import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getAccessToken } from '@/api/client';

export function useFileDownload() {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = useCallback(async (url: string, defaultFilename: string) => {
    setIsDownloading(true);
    try {
      const token = getAccessToken();
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast.error(t('admin.download_permission_denied'));
        } else {
          toast.error(t('admin.download_failed'));
        }
        return;
      }

      // Extract filename from Content-Disposition if present
      const disposition = response.headers.get('Content-Disposition');
      let filename = defaultFilename;
      if (disposition) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(a);
    } catch {
      toast.error(t('admin.download_failed'));
    } finally {
      setIsDownloading(false);
    }
  }, [t]);

  return { downloadFile, isDownloading };
}
